import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { ModalDirective, ModalOptions } from 'ngx-bootstrap/modal';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import * as FileSaver from 'file-saver';

import { LicenseTableKeys, CertificateTableKeys } from './product-instance-specific.types';
import { Customer, ProductInstance, ProductInstanceLicense, ProductInstanceCertificate, LicenseType, Status, FileModel, User } from 'src/api/models';
import { ProductInstanceService, LicenseService, LicenseTypeService, CertificateService, DataBusService } from 'src/api/services';
import { ApplicationConfigService } from 'src/service/application-config.service';

interface LicenseForm {
  expiredDate: string,
  issuedDate: string,
  price: string,
  licenseKey: string,
  licenseNumber: string,
  licenseTypeId: string
}

interface CertificateForm {
  expiredDate: string,
  issuedDate: string,
  certificateName: string
}

@Component({
  selector: 'app-product-instance-specific',
  templateUrl: './product-instance-specific.component.html',
  styleUrls: ['./product-instance-specific.component.scss']
})
export class ProductInstanceSpecificComponent implements OnInit {
  @ViewChild('licenseModal', { static: false }) licenseModal: ModalDirective;
  @ViewChild('certificateModal', { static: false }) certificateModal: ModalDirective;

  public readonly bsDatePickerConfig: object = { dateInputFormat: "YYYY-MM-DDTHH:mm:ss" };

  public productIdParam?: number;
  public productInstanceIdParam?: number;
  public customer?: Customer;
  public productInstance?: ProductInstance;
  public productInstanceAlerts?: Array<Status>;
  public isSubmitting = false;

  // License
  public isUpdatingLicense = false;
  public licenseToUpdate?: ProductInstanceLicense;
    
  public isReplacingLicense = false;
  public licenseToReplace?: ProductInstanceLicense;

  public licenseTypes?: Array<LicenseType>;
  public licenses?: Array<ProductInstanceLicense>;
  public readonly licenseTableKeys = LicenseTableKeys;
  public readonly licenseGroupModel: FormGroup = this.formBuilder.group({
    expiredDate: ['', Validators.required],
    issuedDate: ['', Validators.required],
    price: ['', [Validators.pattern("^[0-9]*$")]],
    licenseKey: [''],
    licenseNumber: [''],
    licenseTypeId: ['', Validators.required],
    extendByMonths: ['']
  });

  get licenseGroupValue(): LicenseForm {
    return this.licenseGroupModel.value;
  }
  
  // Certificate
  public isUpdatingCertificate = false;
  public certificateToUpdate?: ProductInstanceCertificate;
  
  public files: Array<FileModel>;

  public isReplacingCertificate = false;
  public certificateToReplace?: ProductInstanceCertificate;

  public certificates?: Array<ProductInstanceCertificate>;
  public readonly certificateTableKeys = CertificateTableKeys;
  public readonly certificateGroupModel: FormGroup = this.formBuilder.group({
    expiredDate: ['', Validators.required],
    issuedDate: ['', Validators.required],
    certificateName: ['', Validators.required]
  });

  get certificateGroupValue(): CertificateForm {
    return this.certificateGroupModel.value;
  }

  private modalConfig: ModalOptions = {
    backdrop: true,
    ignoreBackdropClick: true,
    keyboard: false
  }

  constructor(
    private productInstanceService: ProductInstanceService,
    private dataBusService: DataBusService,
    private licenseService: LicenseService,
    private licenseTypeService: LicenseTypeService,
    private certificateService: CertificateService,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private app: ApplicationConfigService,
    private formBuilder: FormBuilder,
    private toasterService: ToastrService) { }

  ngOnInit(): void {
    const productInstanceIdParam = parseInt(this.route.snapshot.paramMap.get("productInstanceId"));
    const productIdParam = parseInt(this.route.snapshot.paramMap.get("productId"));
    if (!isNaN(productInstanceIdParam)) {
      this.productInstanceIdParam = productInstanceIdParam;
      this.productIdParam = productIdParam;
      this.getProductInstanceById(productIdParam, productInstanceIdParam);
      this.getFiles(productInstanceIdParam);
    } else {
      // todo - redirect(?) and through toaster?
      this.router.navigate(['/customers']);
    }
  }

  getProductInstanceById(productId: number, productInstanceId: number): void {
    this.productInstanceService.ProductInstanceGetProductInstanceWithStatus({productId:productId, productInstanceId:productInstanceId})
      .subscribe(
        res => {
          this.productInstance = res;
          this.customer = res.customer;
          this.licenses = res.licenses;
          this.certificates = res.certificates;
          this.getLicenseTypes(this.productInstance.product.productId);
          this.formatAlerts(this.licenses, this.certificates);
        },
        err => {
          console.error("ERROR: getProductInstanceById", err);
        }
      )
  }

  getLicenseTypes(productId: number): void {
    this.licenseTypeService.LicenseTypeGetAllLicenseTypesByProductId(productId)
      .subscribe(
        res => {
          this.licenseTypes = res;
        },
        err => {
          console.error("ERROR: getLicenseTypes", err);
        }
      )
  }

  /**
   * Combine all certificate & license statuses into one array to display in Alert component
   * @param licenses  
   * @param certificates
   */
  formatAlerts(licenses: Array<ProductInstanceLicense>, certificates: Array<ProductInstanceCertificate>): void {
    const alerts = new Array<Status>();

    // Check if license/certificates have status, and the status is not OK
    licenses.forEach(license => {
      if (license.status && license.status.level < 2) {
        alerts.push(license.status);
      }
    })

    certificates.forEach(certificate => {
      if (certificate.status && certificate.status.level < 2) {
        alerts.push(certificate.status);
      }
    })
    
    this.productInstanceAlerts = alerts.sort((a, b) => a.level - b.level);
  }

  // Licenses
  /**
   * Open the license modal
   */
  openLicenseModal(): void {
    this.licenseModal.config = this.modalConfig;
    this.licenseModal.show();
  }

  /**
   * Close the license modal, resets create logic.
   */  
  closeLicenseModal(): void {
    this.licenseModal.hide();
    this.licenseGroupModel.reset();
    this.isUpdatingLicense = this.isReplacingLicense = false;
  }

  /**
   * Depending on Form 'Mode' either call to create or edit a license
   */
  saveLicense(): void {
    debugger;
    if (this.isUpdatingLicense) {
      this.updateLicense();
    } else if (this.isReplacingLicense) {
      this.replaceLicense();
    } else {
      this.createLicense();
    }
  }

  /**
   * Enables the Form to edit a specific license
   * @param license
   */
  beginUpdateLicense(license: ProductInstanceLicense): void {
    this.licenseToUpdate = license;

    this.licenseGroupModel.setValue({
      extendByMonths: '',
      expiredDate: license.expiredDate,
      issuedDate: license.issuedDate,
      price: license.price,
      licenseKey: license.licenseKey,
      licenseNumber: license.licenseNumber,
      licenseTypeId: license.licenseType?.licenseTypeId // todo - It looks as if I am not being returned this data from the API. the licenseType object is null.
    })

    this.isUpdatingLicense = true;
    this.openLicenseModal();
  }

  licensePrice: number;
  extendByMonths: number;

  /**
   * Enables the Form to create a new license linked to another
   * @param license
   */
  beginReplaceLicense(license: ProductInstanceLicense): void {
    this.licenseToReplace = license;
    this.isReplacingLicense = true;
    this.licensePrice = license.price;
    this.extendByMonths = 12;
    this.openLicenseModal();
  }

  getFiles(id: number): void{
    this.productInstanceService.ProductInstanceListFiles(id)
    .subscribe(
      res => {
        this.files = res;
      },
      err => {
        console.error("Error: getLicenseFiles", err);
      }
    );
  }

  onLicenseFileDrop(files: Array<NgxFileDropEntry>): void {
    const id = this.licenseToUpdate.productInstanceLicenseId;

    // todo - licenseInsert missing
    this.fileDropHandler(files).file((file: File) => {
      this.sendFile(`/api/License/InsertFile?licenseId=${id}`, file)
        .then(
          res => {
            this.toasterService.success(`Successfully Uploaded License File`, "Upload License File");
          },
          err => {
            console.error("ERROR: onLicenseFileDrop", err);
            this.toasterService.error(`Failed To Upload License File`, "Upload License File");
          }
        );
    })
  }

  downloadLicense(license: ProductInstanceLicense): void {
    this.licenseService.LicenseRetrieveFileResponse(license.productInstanceLicenseId)
      .subscribe(
        res => {
          if (res.body.type !== "text/plain") {
            const blob = new Blob([res.body]);
            FileSaver.saveAs(blob, this.getFileName(res.headers));
          } else {
            this.toasterService.error(`File Not Found`, "Download License File");
          }
        },
        err => {
          this.toasterService.error(`Failed To Download License File`, "Download License File");
          console.error("ERROR: downloadLicense", err);
        }
      )
  }

  downloadLicenseByUri(file: FileModel): void {
    this.licenseService.LicenseGetFileResponse(file.uri)
      .subscribe(
        res => {
          if (res.body.type !== "text/plain") {
            const blob = new Blob([res.body]);
            FileSaver.saveAs(blob, this.getFileName(res.headers));
          } else {
            this.toasterService.error(`File Not Found`, "Download License File");
          }
        },
        err => {
          this.toasterService.error(`Failed To Download License File`, "Download License File");
          console.error("ERROR: downloadLicenseByUri", err);
        }
      )
  }

  /**
   * Searches the Content-Disposition header for a file name
   * @param headers
   */
  getFileName(headers: HttpHeaders): string {
    let filename = "";
    let disposition = headers.get("Content-Disposition");
    if (disposition && disposition.indexOf('attachment') !== -1) {
      var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      var matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) { 
        filename = matches[1].replace(/['"]/g, '');
      }
    }
    return filename;
  }

  /**
   * Add license to product instance, using the form values, updates products instance success
   */
  createLicense(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const args: LicenseService.LicenseCreateProductLicenseParams = {
      model: {
        expiredDate: this.licenseGroupValue.expiredDate,
        issuedDate: this.licenseGroupValue.issuedDate,
        price: parseFloat(this.licenseGroupValue.price),
        productInstanceLicenseId: this.productInstance.productInstanceId,
        licenseKey: this.licenseGroupValue.licenseKey,
        licenseNumber: this.licenseGroupValue.licenseNumber,
        licenseType: { licenseTypeId: parseFloat(this.licenseGroupValue.licenseTypeId) }
      } as ProductInstanceLicense,
      productInstanceId: this.productInstance.productInstanceId,
    };

    this.licenseService.LicenseCreateProductLicense(args)
      .subscribe(
        res => {
          if (res) {
            this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
            this.closeLicenseModal();
            this.toasterService.success(`Successfully Created License`, "Create License");
          } else {
            this.toasterService.warning(`License Must Be Unique`, "Create License");
          }

          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: createLicense", err);
          this.toasterService.error(`Failed To Create License`, "Create License");
          this.isSubmitting = false;
        }
      )
  }

  /**
   * Update specific license, using the licenseToUpdate and form values, updates product instance array on success
   */
  updateLicense(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const args: ProductInstanceLicense = {
      expiredDate: this.licenseGroupValue.expiredDate,
      issuedDate: this.licenseGroupValue.issuedDate,
      price: parseFloat(this.licenseGroupValue.price),
      productInstanceLicenseId: this.licenseToUpdate.productInstanceLicenseId,
      licenseKey: this.licenseGroupValue.licenseKey,
      licenseNumber: this.licenseGroupValue.licenseNumber,
      licenseType: { licenseTypeId: parseFloat(this.licenseGroupValue.licenseTypeId) }
    } as ProductInstanceLicense;

    this.licenseService.LicenseUpdateLicense(args)
      .subscribe(
        res => {
          if (res) {
            this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
            this.closeLicenseModal();
            this.toasterService.success(`Successfully Updated License`, "Update License")
          } else {
            this.toasterService.warning(`License Must Be Unique`, "Update License");
          }

          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: updateLicense", err);
          this.toasterService.error(`Failed To Update License`, "Update License")
          this.isSubmitting = false;
        }
      )
  }

  /**
   * Replace existing license with a new license
   */
  replaceLicense(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    if (this.licenseToReplace.dataBusLicenseId > 0) {
      this.dataBusService.DataBusRenewLicense({customerId: this.app.customerId, licenseId: this.licenseToReplace.dataBusLicenseId}).subscribe(res => {
        if (res) {
          this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
          this.closeLicenseModal();
          this.toasterService.success(`Successfully Replaced License`, "Replace License")
        } else {
          this.toasterService.warning(`License Must Be Unique`, "Replace License");
        }

        this.isSubmitting = false;
      },
      err => {
        console.error("ERROR: replaceLicense", err);
        this.toasterService.error(`Failed To Replace License`, "Replace License")
        this.isSubmitting = false;
      });
    }
    else {
        const args: LicenseService.LicenseReplaceProductLicenseParams = {
          replacedLicenseId: this.licenseToReplace.productInstanceLicenseId,
          newPrice: this.licensePrice,
          extendByMonths: this.extendByMonths
        };

        this.licenseService.LicenseReplaceProductLicense(args)
          .subscribe(
            res => {
              if (res) {
                this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
                this.closeLicenseModal();
                this.toasterService.success(`Successfully Replaced License`, "Replace License")
              } else {
                this.toasterService.warning(`License Must Be Unique`, "Replace License");
              }

              this.isSubmitting = false;
            },
            err => {
              console.error("ERROR: replaceLicense", err);
              this.toasterService.error(`Failed To Replace License`, "Replace License")
              this.isSubmitting = false;
            }
          );
    }
  }

  /**
   * Delete specific product instance license, updates product instance on success
   * @param license
   */
  deleteLicense(license: ProductInstanceLicense): void {
    this.licenseService.LicenseDeleteLicenseById(license.productInstanceLicenseId)
      .subscribe(
        res => {
          this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
          this.toasterService.success(`Successfully Deleted License`, "Delete License")
        },
        err => {
          console.error("ERROR: deleteLicense", err);
          this.toasterService.error(`Failed To Delete License`, "Delete License")
        }
      )
  }  

  // Certificates
  /**
   * Open the certificate modal
   */
  openCertificateModal(): void {
    this.certificateModal.config = this.modalConfig;
    this.certificateModal.show();
  }

  /**
   * Close the certificate modal, resets create logic.
   */  
  closeCertificateModal(): void {
    this.certificateModal.hide();
    this.certificateGroupModel.reset();
    this.isUpdatingCertificate = false;
  }

  /**
   * Depending on Form 'Mode' either call to create or edit a certificate
   */
  saveCertificate(): void {
    if (this.isUpdatingCertificate) {
      this.updateCertificate();
    } else if (this.isReplacingCertificate) {
      this.replaceCertificate();
    } else {
      this.createCertificate(); 
    }
  }

  /**
   * Enables the Form to edit a specific certificate
   * @param certificate
   */
  beginUpdateCertificate(certificate: ProductInstanceCertificate): void {
    this.certificateToUpdate = certificate;

    this.certificateGroupModel.setValue({
      expiredDate: certificate.expiredDate,
      issuedDate: certificate.issuedDate,
      certificateName: certificate.certificateName
    } as ProductInstanceCertificate)

    this.isUpdatingCertificate = true;
    this.openCertificateModal();
  }
  /**
   * Enables the Form to create a new license linked to another
   * @param certificate
   */
  beginReplaceCertificate(certificate: ProductInstanceCertificate): void {
    this.certificateToReplace = certificate;
    this.isReplacingCertificate= true;
    this.openCertificateModal();
  }


  onCertificateFileDrop(files: Array<NgxFileDropEntry>) {
    const id = this.certificateToUpdate.certificateId;

    this.fileDropHandler(files).file((file: File) => {
      this.sendFile(`/api/Certificate/InsertFile?certificateId=${id}`, file).then(
        res => {
          this.toasterService.success(`Successfully Uploaded Certificate File`, "Upload Certificate File");
        },
        err => {
          console.error("ERROR: onCertificateFileDrop", err);
          this.toasterService.error(`Failed To Upload Certificate File`, "Upload Certificate File");
        }
      );
    })
  }

  downloadCertificate(certificate: ProductInstanceCertificate): void {
    this.certificateService.CertificateRetrieveFileResponse(certificate.certificateId)
      .subscribe(
        res => {
          if (res.body.type !== "text/plain") {
            const blob = new Blob([res.body]);
            FileSaver.saveAs(blob, this.getFileName(res.headers));
          } else {
            this.toasterService.error(`File Not Found`, "Download Certificate File");
          }
        },
        err => {
          this.toasterService.error(`Failed To Download Certificate File`, "Download Certificate File");
          console.error("ERROR: downloadCertificate", err);
        }
      )
  }

  downloadFileByUri(file: FileModel){
    console.log(file);
    if (file.fileType == "certificate"){
      this.downloadCertificateByUri(file);
    }else{
      this.downloadLicenseByUri(file);
    }
  }

  downloadCertificateByUri(file: FileModel): void {
    this.certificateService.CertificateGetFileResponse(file.uri)
      .subscribe(
        res => {
          if (res.body.type !== "text/plain") {
            const blob = new Blob([res.body]);
            FileSaver.saveAs(blob, this.getFileName(res.headers));
          } else {
            this.toasterService.error(`File Not Found`, "Download Certificate File");
          }
        },
        err => {
          this.toasterService.error(`Failed To Download Certificate File`, "Download Certificate File");
          console.error("ERROR: downloadCertificateByUri", err);
        }
      )
  }

  /**
   * Add certificate to product instance, using the form values, updates certificates array on success
   */
  createCertificate(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const args: CertificateService.CertificateCreateProductCerificateParams = {
      model: {
        expiredDate: this.certificateGroupValue.expiredDate,
        issuedDate: this.certificateGroupValue.issuedDate,
        certificateName: this.certificateGroupValue.certificateName,
        certificateId: 0
      } as ProductInstanceCertificate,
      productInstanceId: this.productInstance.productInstanceId
    };

    this.certificateService.CertificateCreateProductCerificate(args)
      .subscribe(
        res => {
          if (res) {
            this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
            this.closeCertificateModal();
            this.toasterService.success(`Successfully Created Certificate`, "Create Certificate")
          } else {
            this.toasterService.warning(`Certificate Must Be Unique`, "Create Certificate")
          }

          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: createLicense", err);
          this.toasterService.error(`Failed To Create Certificate`, "Create Certificate")
          this.isSubmitting = false;
        }
      )
  }

  /**
   * Update specific certificate, using the customerToUpdate and form values, updates certificates array on success
   */
  updateCertificate(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const args: ProductInstanceCertificate = {
      expiredDate: this.certificateGroupValue.expiredDate,
      issuedDate: this.certificateGroupValue.issuedDate,
      certificateName: this.certificateGroupValue.certificateName,
      certificateId: this.certificateToUpdate.certificateId,
    } as ProductInstanceCertificate;

    this.certificateService.CertificateUpdateCertificate(args)
      .subscribe(
        res => {
          if (res) {
            this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
            this.closeCertificateModal();
            this.toasterService.success(`Successfully Updated Certificate`, "Update Certificate");
          } else {
            this.toasterService.warning(`Certificate Must Be Unique`, "Update Certificate")
          }

          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: updateCertificate", err);
          this.toasterService.error(`Failed To Update Certificate`, "Update Certificate");
          this.isSubmitting = false;
        }
      )
  }  

  /**
   * Replace existing license with a new license
   */
  replaceCertificate(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const args: CertificateService.CertificateReplaceProductCerificateParams = {
      existingCertificateId: this.certificateToReplace.certificateId,
      model: {
        expiredDate: this.certificateGroupValue.expiredDate,
        issuedDate: this.certificateGroupValue.issuedDate,
        certificateName: this.certificateGroupValue.certificateName,
        certificateId: 0,
      } as ProductInstanceCertificate
    };

    this.certificateService.CertificateReplaceProductCerificate(args)
      .subscribe(
        res => {
          if (res) {
            this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
            this.closeCertificateModal();
            this.toasterService.success(`Successfully Replaced Certificate`, "Replace Certificate");
          } else {
            this.toasterService.warning(`Certificate Must Be Unique`, "Replace Certificate");
          }

          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: replaceCertificate", err);
          this.toasterService.error(`Failed To Replace Certificate`, "Replace Certificate");
          this.isSubmitting = false;
        }
      )
  }  

  /**
   * Delete specific product instance certificate, updates certificates array on success
   * @param certificate
   */
  deleteCertificate(certificate: ProductInstanceCertificate): void {
    this.certificateService.CertificateDeleteCertificateById(certificate.certificateId)
      .subscribe(
        res => {
          this.getProductInstanceById(this.productInstance.product.productId, this.productInstance.productInstanceId);
          this.toasterService.success(`Successfully Deleted Certificate`, "Delete Certificate");
        },
        err => {
          console.error("ERROR: deleteCertificate", err);
          this.toasterService.error(`Failed To Delete Certificate`, "Delete Certificate");
        }
      )
  }  

  // File Handling
  fileDropHandler(files: Array<NgxFileDropEntry>): FileSystemFileEntry {
    if (files.length > 1) {
      this.toasterService.error("Cannot upload multiple files", "Upload File");
      return null;
    }

    let droppedFile = files[0];

    if (!droppedFile.fileEntry.isFile) {
      this.toasterService.error("Uploaded file is not a valid file", "Upload File");
    }

    return droppedFile.fileEntry as FileSystemFileEntry;
  }

  async sendFile(uri: string, file: File): Promise<Object> {
    const fd = new FormData();
    fd.append('asset', file, file.name)
    
    return await this.http.post(uri, fd).toPromise();
  }
}